import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaCoins, FaLock, FaChartLine, FaClock } from 'react-icons/fa';
import { useAccount, useBalance, useReadContract, useWriteContract, useWaitForTransactionReceipt } from 'wagmi';
import { parseUnits, formatUnits } from 'viem';
import axios from 'axios';
import stakingABI from '../abi/Staking.json';
import { useAutoSwitchNetwork } from '../hooks/useAutoSwitchNetwork';
import './Staking.css';

const Staking = () => {
  const { address, isConnected } = useAccount();
  useAutoSwitchNetwork(); // Auto-switch to Avalanche
  const [activeTab, setActiveTab] = useState('stake');
  const [stakeAmount, setStakeAmount] = useState('');
  const [unstakeAmount, setUnstakeAmount] = useState('');
  const [apr, setApr] = useState(0);
  const [prices, setPrices] = useState({ velai: 0.01, avax: 23 }); // Default prices to prevent $0 display
  const [needsApproval, setNeedsApproval] = useState(false);
  const [txStatus, setTxStatus] = useState('');
  const [pendingStakeAfterApproval, setPendingStakeAfterApproval] = useState(false);

  // Contract addresses from environment variables
  const STAKING_CONTRACT = process.env.REACT_APP_STAKING_CONTRACT || '0x01731b3122D5dE9455404311F785224195Adf2D8';
  const VELAI_TOKEN = process.env.REACT_APP_VELAI_TOKEN || '0x1c7c53aa86B49A28C627B6450091998e447A42f9';

  // Fetch VELAI balance
  const { data: velaiBalance, refetch: refetchVelaiBalance } = useBalance({
    address: address,
    token: VELAI_TOKEN,
    watch: true,
    enabled: isConnected,
  });

  // Read contract states
  const { data: totalStaked, refetch: refetchTotalStaked } = useReadContract({
    address: STAKING_CONTRACT,
    abi: stakingABI,
    functionName: 'totalStaked',
    watch: true,
  });

  const { data: rewardEmissionRate } = useReadContract({
    address: STAKING_CONTRACT,
    abi: stakingABI,
    functionName: 'rewardEmissionRate',
    watch: true,
  });

  const { data: userInfo, refetch: refetchUserInfo } = useReadContract({
    address: STAKING_CONTRACT,
    abi: stakingABI,
    functionName: 'userInfo',
    args: address ? [address] : undefined,
    watch: true,
    enabled: isConnected && !!address,
  });

  const { data: pendingRewards, refetch: refetchPendingRewards } = useReadContract({
    address: STAKING_CONTRACT,
    abi: stakingABI,
    functionName: 'pendingRewards',
    args: address ? [address] : undefined,
    watch: true,
    enabled: isConnected && !!address,
  });

  const { data: rewardTotalPending, refetch: refetchRewardTotalPending } = useReadContract({
    address: STAKING_CONTRACT,
    abi: stakingABI,
    functionName: 'rewardTotalPending',
    watch: true,
  });

  // Check token allowance
  const { data: tokenAllowance } = useReadContract({
    address: VELAI_TOKEN,
    abi: [
      {
        inputs: [
          { internalType: 'address', name: 'owner', type: 'address' },
          { internalType: 'address', name: 'spender', type: 'address' },
        ],
        name: 'allowance',
        outputs: [{ internalType: 'uint256', name: '', type: 'uint256' }],
        stateMutability: 'view',
        type: 'function',
      },
    ],
    functionName: 'allowance',
    args: address && [address, STAKING_CONTRACT],
    enabled: isConnected && !!address,
    watch: true,
  });

  // Write contract hooks - separate for each action
  const { writeContract: writeApprove, data: approvalHash, isPending: isApprovePending } = useWriteContract();
  const { writeContract: writeStake, data: stakeHash, isPending: isStakePending } = useWriteContract();
  const { writeContract: writeUnstake, data: unstakeHash, isPending: isUnstakePending } = useWriteContract();
  const { writeContract: writeClaim, data: claimHash, isPending: isClaimPending } = useWriteContract();

  // Track approval transaction
  const { isLoading: isApprovalWaiting, isSuccess: approvalSuccess } = useWaitForTransactionReceipt({
    hash: approvalHash,
  });

  // Track stake transaction
  const { isLoading: isStakeWaiting, isSuccess: stakeSuccess } = useWaitForTransactionReceipt({
    hash: stakeHash,
  });

  // Track unstake transaction
  const { isLoading: isUnstakeWaiting, isSuccess: unstakeSuccess } = useWaitForTransactionReceipt({
    hash: unstakeHash,
  });

  // Track claim transaction
  const { isLoading: isClaimWaiting, isSuccess: claimSuccess } = useWaitForTransactionReceipt({
    hash: claimHash,
  });

  // Set page title
  useEffect(() => {
    document.title = 'Stake VELAI | Velvet AI';
  }, []);

  // Fetch prices from API
  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const response = await axios.get(`${apiUrl}/prices`);
        console.log('Fetched prices:', response.data);
        if (response.data.success && response.data.data) {
          const velaiPrice = response.data.data.velai?.priceUsd || 0;
          const avaxPrice = response.data.data.avax?.priceUsd || 0;

          console.log('Setting prices - VELAI:', velaiPrice, 'AVAX:', avaxPrice);
          setPrices({
            velai: velaiPrice,
            avax: avaxPrice,
          });
        }
      } catch (error) {
        console.error('Error fetching prices from API:', error);
        // Try direct fetch from DexScreener as fallback
        try {
          const dexScreenerApi = process.env.REACT_APP_DEXSCREENER_API || 'https://api.dexscreener.com';
          const velaiToken = process.env.REACT_APP_VELAI_TOKEN || '0x1c7c53aa86B49A28C627B6450091998e447A42f9';
          const dexResponse = await axios.get(`${dexScreenerApi}/latest/dex/tokens/${velaiToken}`);
          if (dexResponse.data?.pairs?.[0]) {
            const velaiPrice = parseFloat(dexResponse.data.pairs[0].priceUsd) || 0;
            console.log('Fallback VELAI price from DexScreener:', velaiPrice);
            setPrices((prev) => ({ ...prev, velai: velaiPrice }));
          }
        } catch (dexError) {
          console.error('Fallback fetch also failed:', dexError);
        }
      }
    };

    fetchPrices();
    const interval = setInterval(fetchPrices, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);

  // Calculate APR
  useEffect(() => {
    if (totalStaked && rewardEmissionRate && prices.velai && prices.avax) {
      const totalStakedNum = parseFloat(formatUnits(totalStaked || 0n, 18));
      const emissionRateNum = parseFloat(formatUnits(rewardEmissionRate || 0n, 18));

      if (totalStakedNum > 0) {
        // Annual rewards in AVAX
        const annualRewards = emissionRateNum * 365 * 24 * 3600;
        const annualRewardsValue = annualRewards * prices.avax;

        // Total staked value in USD
        const totalStakedValue = totalStakedNum * prices.velai;

        // APR calculation
        const calculatedApr = (annualRewardsValue / totalStakedValue) * 100;
        setApr(Math.min(calculatedApr, 999)); // Cap at 999% to avoid display issues
      }
    }
  }, [totalStaked, rewardEmissionRate, prices]);

  // Check if approval is needed
  useEffect(() => {
    if (stakeAmount && tokenAllowance !== undefined) {
      try {
        const amountToStake = parseUnits(stakeAmount, 18);
        setNeedsApproval(tokenAllowance < amountToStake);
      } catch (e) {
        setNeedsApproval(false);
      }
    } else {
      setNeedsApproval(false);
    }
  }, [stakeAmount, tokenAllowance]);

  // Handle approval success - immediately trigger stake
  useEffect(() => {
    if (approvalSuccess && pendingStakeAfterApproval && stakeAmount) {
      console.log('Approval successful, immediately triggering stake transaction');
      setNeedsApproval(false);
      setPendingStakeAfterApproval(false);

      // Immediately trigger the stake transaction
      const executeStake = async () => {
        try {
          setTxStatus('confirming');
          await writeStake({
            address: STAKING_CONTRACT,
            abi: stakingABI,
            functionName: 'deposit',
            args: [parseUnits(stakeAmount, 18)],
          });
        } catch (error) {
          console.error('Staking after approval failed:', error);
          setTxStatus('');
        }
      };

      executeStake();
    }
  }, [approvalSuccess, pendingStakeAfterApproval, stakeAmount, writeStake, STAKING_CONTRACT]);

  // Reset states on stake/unstake/claim success
  useEffect(() => {
    if (stakeSuccess || unstakeSuccess || claimSuccess) {
      setTxStatus('success');

      // Refetch all contract data after successful transaction
      refetchVelaiBalance();
      refetchUserInfo();
      refetchPendingRewards(); // This is crucial - rewards are claimed on stake/unstake/claim
      refetchTotalStaked();
      refetchRewardTotalPending();

      setTimeout(() => {
        setStakeAmount('');
        setUnstakeAmount('');
        setTxStatus('');
        setPendingStakeAfterApproval(false);
      }, 3000);
    }
  }, [stakeSuccess, unstakeSuccess, claimSuccess, refetchVelaiBalance, refetchUserInfo, refetchPendingRewards, refetchTotalStaked, refetchRewardTotalPending]);

  const handleApprove = async () => {
    if (!isConnected || !stakeAmount) return;

    try {
      setTxStatus('approving');

      const approveABI = [
        {
          inputs: [
            { internalType: 'address', name: 'spender', type: 'address' },
            { internalType: 'uint256', name: 'amount', type: 'uint256' },
          ],
          name: 'approve',
          outputs: [{ internalType: 'bool', name: '', type: 'bool' }],
          stateMutability: 'nonpayable',
          type: 'function',
        },
      ];

      await writeApprove({
        address: VELAI_TOKEN,
        abi: approveABI,
        functionName: 'approve',
        args: [STAKING_CONTRACT, parseUnits(stakeAmount, 18)],
      });
    } catch (error) {
      console.error('Approval failed:', error);
      setTxStatus('');
      setPendingStakeAfterApproval(false);
    }
  };

  const handleStake = async () => {
    if (!isConnected || !stakeAmount) return;

    // Check if approval is needed first
    if (needsApproval) {
      setPendingStakeAfterApproval(true); // Set flag to continue with stake after approval
      await handleApprove();
      return;
    }

    try {
      setTxStatus('confirming');

      await writeStake({
        address: STAKING_CONTRACT,
        abi: stakingABI,
        functionName: 'deposit',
        args: [parseUnits(stakeAmount, 18)],
      });

      // Note: The contract automatically claims pending rewards when staking
      // The refetch in the success effect will update the UI
    } catch (error) {
      console.error('Staking failed:', error);
      setTxStatus('');
      setPendingStakeAfterApproval(false);
    }
  };

  const handleUnstake = async () => {
    if (!isConnected || !unstakeAmount) return;

    try {
      setTxStatus('confirming');

      await writeUnstake({
        address: STAKING_CONTRACT,
        abi: stakingABI,
        functionName: 'withdraw',
        args: [parseUnits(unstakeAmount, 18)],
      });

      // Note: The contract automatically claims pending rewards when unstaking
      // The refetch in the success effect will update the UI
    } catch (error) {
      console.error('Unstaking failed:', error);
      setTxStatus('');
    }
  };

  const handleClaimRewards = async () => {
    if (!isConnected) return;

    try {
      setTxStatus('confirming');

      await writeClaim({
        address: STAKING_CONTRACT,
        abi: stakingABI,
        functionName: 'harvestRewards',
      });
    } catch (error) {
      console.error('Claiming failed:', error);
      setTxStatus('');
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(2)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(0)}K`;
    }
    return num.toFixed(2);
  };

  const formatUSD = (value) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(2)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${value.toFixed(2)}`;
  };

  return (
    <div className='staking'>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <h1 className='page-title'>VELAI Staking</h1>
        <p className='page-subtitle'>Stake your VELAI tokens and earn AVAX rewards</p>

        <div className='staking-stats'>
          <div className='stat-card'>
            <div className='stat-icon'>
              <FaChartLine />
            </div>
            <div className='stat-content'>
              <div className='stat-label'>Current APR</div>
              <div className='stat-value'>{apr.toFixed(2)}%</div>
            </div>
          </div>

          <div className='stat-card'>
            <div className='stat-icon'>
              <FaCoins />
            </div>
            <div className='stat-content'>
              <div className='stat-label'>Total Staked</div>
              <div className='stat-value'>{formatNumber(parseFloat(formatUnits(totalStaked || 0n, 18)))} VELAI</div>
              <div className='stat-usd'>{formatUSD(parseFloat(formatUnits(totalStaked || 0n, 18)) * prices.velai)}</div>
            </div>
          </div>

          <div className='stat-card'>
            <div className='stat-icon'>
              <FaLock />
            </div>
            <div className='stat-content'>
              <div className='stat-label'>Pending Rewards Pool*</div>
              <div className='stat-value'>{formatNumber(parseFloat(formatUnits(rewardTotalPending || 0n, 18)))} AVAX</div>
              <div className='stat-usd'>{formatUSD(parseFloat(formatUnits(rewardTotalPending || 0n, 18)) * prices.avax)}</div>
            </div>
          </div>
        </div>

        {/* Side-by-side Layout Container */}
        {isConnected && (
          <div className='staking-main-layout'>
            {/* Left Panel - User Info */}
            <div className='staking-info-panel'>
              <h3 className='staking-panel-title'>Your Staking Info</h3>
              <div className='staking-info-stats'>
                <div className='staking-info-item'>
                  <span className='staking-info-label'>Staked Balance</span>
                  <span className='staking-info-value'>
                    {isStakeWaiting || isUnstakeWaiting ? 'Updating...' : formatNumber(parseFloat(formatUnits(userInfo?.[0] || 0n, 18)))}
                  </span>
                  <span className='staking-info-token'>VELAI</span>
                  <span className='staking-info-usd'>
                    {isStakeWaiting || isUnstakeWaiting ? '' : formatUSD(parseFloat(formatUnits(userInfo?.[0] || 0n, 18)) * prices.velai)}
                  </span>
                </div>
                <div className='staking-info-item'>
                  <span className='staking-info-label'>Earned Rewards</span>
                  <span className='staking-info-value'>
                    {isStakeWaiting || isUnstakeWaiting || isClaimWaiting ? 'Updating...' : parseFloat(formatUnits(pendingRewards || 0n, 18)).toFixed(6)}
                  </span>
                  <span className='staking-info-token'>AVAX</span>
                  <span className='staking-info-usd'>
                    {isStakeWaiting || isUnstakeWaiting || isClaimWaiting ? '' : formatUSD(parseFloat(formatUnits(pendingRewards || 0n, 18)) * prices.avax)}
                  </span>
                </div>
              </div>
              {pendingRewards && pendingRewards > 0n && (
                <button className='staking-claim-button' onClick={handleClaimRewards} disabled={isClaimPending || isClaimWaiting}>
                  {isClaimPending ? 'Confirm in Wallet...' : isClaimWaiting ? 'Processing...' : 'Claim Rewards'}
                </button>
              )}
            </div>

            {/* Right Panel - Staking Interface */}
            <div className='staking-action-panel'>
              <div className='staking-tabs'>
                <button 
                  className={`staking-tab ${activeTab === 'stake' ? 'active' : ''}`} 
                  onClick={() => setActiveTab('stake')}
                >
                  Stake
                </button>
                <button 
                  className={`staking-tab ${activeTab === 'unstake' ? 'active' : ''}`} 
                  onClick={() => setActiveTab('unstake')}
                >
                  Unstake
                </button>
              </div>

          <div className='staking-tab-content'>
            {activeTab === 'stake' ? (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }} className='staking-form-container'>
                <div className='staking-input-section'>
                  <label className='staking-label'>Amount to Stake</label>
                  <div className='staking-input-wrapper'>
                    <input 
                      type='number' 
                      className='staking-input' 
                      placeholder='0.0' 
                      value={stakeAmount} 
                      onChange={(e) => setStakeAmount(e.target.value)} 
                    />
                    <span className='staking-token-badge'>VELAI</span>
                  </div>
                  <div className='staking-balance-row'>
                    <span className='staking-balance-text'>
                      Available: {velaiBalance ? parseFloat(velaiBalance.formatted).toFixed(4) : '0'} VELAI
                    </span>
                    <button
                      className='staking-max-button'
                      onClick={() => {
                        if (velaiBalance) {
                          const balance = parseFloat(velaiBalance.formatted);
                          setStakeAmount((Math.floor(balance * 10000) / 10000).toString());
                        }
                      }}
                    >
                      MAX
                    </button>
                  </div>
                </div>

                <button className='btn-primary stake-btn' onClick={handleStake} disabled={!stakeAmount || parseFloat(stakeAmount) <= 0 || isApprovePending || isApprovalWaiting || isStakePending || isStakeWaiting}>
                  {!isConnected
                    ? 'Connect Wallet'
                    : isApprovePending || isApprovalWaiting
                    ? 'Approving...'
                    : needsApproval
                    ? `Approve VELAI`
                    : isStakePending
                    ? 'Confirm in Wallet...'
                    : isStakeWaiting
                    ? 'Processing...'
                    : stakeSuccess && txStatus === 'success'
                    ? 'Success!'
                    : 'Stake VELAI'}
                </button>

                {txStatus === 'success' && (approvalHash || stakeHash) && (
                  <div className='transaction-success'>
                    <span>✅ Transaction Successful!</span>
                    <a href={`https://snowtrace.io/tx/${stakeHash || approvalHash}`} target='_blank' rel='noopener noreferrer' className='tx-link'>
                      View on Snowtrace
                    </a>
                  </div>
                )}
              </motion.div>
            ) : (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }} className='staking-form-container'>
                <div className='staking-input-section'>
                  <label className='staking-label'>Amount to Unstake</label>
                  <div className='staking-input-wrapper'>
                    <input 
                      type='number' 
                      className='staking-input' 
                      placeholder='0.0' 
                      value={unstakeAmount} 
                      onChange={(e) => setUnstakeAmount(e.target.value)} 
                    />
                    <span className='staking-token-badge'>VELAI</span>
                  </div>
                  <div className='staking-balance-row'>
                    <span className='staking-balance-text'>
                      Staked: {userInfo ? formatNumber(parseFloat(formatUnits(userInfo[0], 18))) : '0'} VELAI
                    </span>
                    <button
                      className='staking-max-button'
                      onClick={() => {
                        if (userInfo && userInfo[0]) {
                          const staked = parseFloat(formatUnits(userInfo[0], 18));
                          setUnstakeAmount((Math.floor(staked * 10000) / 10000).toString());
                        }
                      }}
                    >
                      MAX
                    </button>
                  </div>
                </div>

                <div className='warning-message'>
                  <FaClock />
                  <span>Make sure to claim your rewards before unstaking</span>
                </div>

                <button className='btn-primary unstake-btn' onClick={handleUnstake} disabled={!unstakeAmount || parseFloat(unstakeAmount) <= 0 || isUnstakePending || isUnstakeWaiting}>
                  {!isConnected ? 'Connect Wallet' : isUnstakePending ? 'Confirm in Wallet...' : isUnstakeWaiting ? 'Processing...' : unstakeSuccess && txStatus === 'success' ? 'Success!' : 'Unstake VELAI'}
                </button>

                {txStatus === 'success' && unstakeHash && (
                  <div className='transaction-success'>
                    <span>✅ Transaction Successful!</span>
                    <a href={`https://snowtrace.io/tx/${unstakeHash}`} target='_blank' rel='noopener noreferrer' className='tx-link'>
                      View on Snowtrace
                    </a>
                  </div>
                )}
              </motion.div>
            )}
              </div>
            </div>
          </div>
        )}

        {/* If not connected, show connect message */}
        {!isConnected && (
          <div className='staking-connect-message'>
            <h3>Connect Your Wallet</h3>
            <p>Please connect your wallet to start staking VELAI tokens</p>
          </div>
        )}

        <div className='staking-faq glass-card'>
          <h3>Staking Information</h3>
          <div className='faq-grid'>
            <div className='faq-item'>
              <h4>How does staking work?</h4>
              <p>Stake your VELAI tokens to earn AVAX rewards. Rewards are distributed proportionally based on your stake.</p>
            </div>
            <div className='faq-item'>
              <h4>When can I claim rewards?</h4>
              <p>Rewards accrue in real-time and can be claimed at any time without unstaking your tokens.</p>
            </div>
            <div className='faq-item'>
              <h4>What determines the APR?</h4>
              <p>APR is calculated based on the reward emission rate, total staked amount, and current token prices. It adjusts dynamically.</p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Staking;
